import { useTranslation } from 'react-i18next'
import { useEffect, useState, useCallback, useRef } from 'react'
import SubVisual from '../../components/SubVisual'
import Img1 from '../../assets/images/ai/img1.jpg'
import Img2 from '../../assets/images/ai/img2.jpg'
import { Link } from 'react-router-dom'
import LogoTakumi from '../../assets/images/ai/takumi_logo_WH.png'

const AiMotionStudio = () => {
	const { t, i18n } = useTranslation()
	const [visibleSections, setVisibleSections] = useState<string[]>([])
	const [horizontalScrollPosition, setHorizontalScrollPosition] = useState(0)
	const [isInHorizontalSection, setIsInHorizontalSection] = useState(false)
	const [horizontalCompleted, setHorizontalCompleted] = useState(false) // 횡 스크롤 완료 상태
	const horizontalSectionRef = useRef<HTMLDivElement>(null)
	const scrollPositionRef = useRef(0)

	const handleIntersection = useCallback(
		(entries: IntersectionObserverEntry[]) => {
			const newSections: string[] = []
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					const sectionId = entry.target.getAttribute('data-section')
					if (sectionId) {
						newSections.push(sectionId)
					}
				}
			})

			if (newSections.length > 0) {
				setVisibleSections((prev) => {
					const updated = [...prev]
					newSections.forEach((section) => {
						if (!updated.includes(section)) {
							updated.push(section)
						}
					})
					return updated
				})
			}
		},
		[],
	)

	// 마우스 휠로 파이프라인 스크롤 제어 (느슨한 설정) - 데스크톱 전용
	const handlePipelineScroll = useCallback(
		(e: WheelEvent) => {
			// 모바일에서는 가로 스크롤 비활성화
			if (window.innerWidth < 768) return
			if (!horizontalSectionRef.current) return

			const rect = horizontalSectionRef.current.getBoundingClientRect()
			const viewportHeight = window.innerHeight

			// 섹션이 화면 중간에 위치할 때 활성화 (화면 높이의 50% 지점 기준)
			const viewportMiddle = viewportHeight / 2
			const isInViewport =
				rect.top <= viewportMiddle && rect.bottom >= viewportMiddle

			// 10%만 로그 출력으로 성능 최적화
			if (Math.random() < 0.1) {
				console.log('🔍 파이프라인 섹션 상태:', {
					top: rect.top.toFixed(0),
					bottom: rect.bottom.toFixed(0),
					viewportMiddle: viewportMiddle.toFixed(0),
					active: isInViewport,
					inHorizontal: isInHorizontalSection,
				})
			}

			if (isInViewport) {
				const scrollSpeed = 2 // 스크롤 속도를 느리게 조정
				const stepWidth = window.innerWidth * 0.6 // 각 스텝 간격을 줄여서 더 부드럽게
				const maxScroll = stepWidth * 2.5 // 4단계를 위한 충분한 스크롤 범위

				// 현재 가로 스크롤 위치 확인 (ref 사용으로 의존성 문제 해결)
				const currentPosition = scrollPositionRef.current

				// 4번째 단계 완료 후 세로 스크롤 전환을 위한 더 관대한 조건
				const completionThreshold = maxScroll * 0.85 // 85% 지점에서 전환 시작
				const isNearEnd = currentPosition >= completionThreshold
				const isScrollingDown = e.deltaY > 0
				const isScrollingUp = e.deltaY < 0

				// 횡 스크롤이 완료된 상태에서는 세로 스크롤만 허용
				if (horizontalCompleted) {
					console.log('� 횡 스크롤 완료됨 - 세로 스크롤만 허용')
					return // preventDefault 하지 않고 기본 세로 스크롤만 허용
				}

				// 4번째 단계 완료 시점 체크
				if (isNearEnd && isScrollingDown) {
					// 4번째 단계 완료 후 바로 세로 스크롤 허용
					console.log('🔄 파이프라인 4단계 완료 - 세로 스크롤로 전환')
					setHorizontalCompleted(true) // 횡 스크롤 완료 상태로 설정
					setIsInHorizontalSection(false)
					return // preventDefault 하지 않고 기본 세로 스크롤 허용
				}

				// 가로 스크롤이 시작 위치이고 위로 스크롤하려는 경우
				const isAtStart = currentPosition <= 0

				if (isAtStart && isScrollingUp) {
					// 가로 스크롤이 시작 위치이므로 세로 스크롤 허용
					console.log('🔄 파이프라인 시작 - 세로 스크롤로 전환')
					setIsInHorizontalSection(false)
					return // preventDefault 하지 않고 기본 세로 스크롤 허용
				}

				// 일반적인 가로 스크롤 처리
				e.preventDefault() // 기본 세로 스크롤 차단

				setHorizontalScrollPosition((prev) => {
					const delta = e.deltaY * scrollSpeed
					const newPosition = Math.max(0, Math.min(maxScroll, prev + delta))

					// ref 업데이트
					scrollPositionRef.current = newPosition

					// 간단한 디버깅 (변화가 클 때만)
					if (Math.abs(newPosition - prev) > 30) {
						const progress = (newPosition / maxScroll) * 100
						const step = Math.min(4, Math.floor((newPosition / stepWidth) * 1.6) + 1)
						console.log(`🎯 파이프라인 Step ${step}/4 (${progress.toFixed(1)}%)`)

						// 역방향 스크롤 상태 체크
						if (isScrollingUp && newPosition < completionThreshold) {
							console.log('↗️ 역방향 가로 스크롤 활성화됨')
						}
					}

					return newPosition
				})

				setIsInHorizontalSection(true)
			} else {
				// 섹션을 벗어나면 모든 상태 리셋
				if (horizontalCompleted) {
					console.log('🔄 섹션 벗어남 - 횡 스크롤 상태 리셋')
					setHorizontalCompleted(false)
					setHorizontalScrollPosition(0)
					scrollPositionRef.current = 0
				}
				setIsInHorizontalSection(false)
			}
		},
		[isInHorizontalSection, horizontalCompleted],
	)

	useEffect(() => {
		// 파이프라인 스크롤 이벤트 리스너 추가
		window.addEventListener('wheel', handlePipelineScroll, { passive: false })

		return () => {
			window.removeEventListener('wheel', handlePipelineScroll)
		}
	}, [handlePipelineScroll])

	useEffect(() => {
		const observer = new IntersectionObserver(handleIntersection, {
			threshold: 0.15,
			rootMargin: '0px 0px -5% 0px',
		})

		const timeoutId = setTimeout(() => {
			document
				.querySelectorAll('[data-section]')
				.forEach((el) => observer.observe(el))
		}, 100)

		return () => {
			clearTimeout(timeoutId)
			observer.disconnect()
		}
	}, [handleIntersection])

	return (
		<div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900 text-white overflow-x-hidden touch-action-manipulation">
			{/* 서브 비주얼 */}
			<SubVisual
				backgroundVideo="/videos/ai_advertise.mp4"
				title={t('page.aiMotionStudio.title')}
				subtitle={t('page.aiMotionStudio.description')}
				height="h-[60vh]"
				overlayOpacity={0.6}
			/>

			{/* 메인 소개 섹션 */}
			<section
				data-section="main-intro"
				className={`relative w-full py-20 px-6 lg:px-12 xl:px-20 transition-all duration-1000 ${
					visibleSections.includes('main-intro')
						? 'opacity-100 translate-y-0'
						: 'opacity-0 translate-y-12'
				}`}
			>
				{/* 배경 그라데이션 */}
				<div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 via-pink-600/10 to-purple-600/10 rounded-3xl blur-3xl"></div>

				<div className="relative container mx-auto text-center space-y-8">
					<div
						className={`transition-all duration-1000 delay-200 ${
							visibleSections.includes('main-intro')
								? 'opacity-100 translate-y-0'
								: 'opacity-0 translate-y-8'
						}`}
					>
						<h2 className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent mb-6">
							{t('page.aiMotionStudio.t1')}
						</h2>
						<p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
							{t('page.aiMotionStudio.t2')}
						</p>
					</div>
				</div>
			</section>

			{/* AI Pipeline 프로세스 섹션 - 데스크톱 */}
			<section
				ref={horizontalSectionRef}
				data-section="process"
				className="hidden md:block w-full bg-gradient-to-br from-slate-900 via-purple-900/30 to-slate-900 py-20 overflow-hidden"
			>
				{/* 제목 */}
				<div className="text-center mb-16">
					<h3 className="text-4xl md:text-5xl font-bold text-white mb-4">
						{t('page.aiMotionStudio.t3')}
					</h3>
					<p className="text-xl text-gray-300">{t('page.aiMotionStudio.t4')}</p>
				</div>

				{/* 파이프라인 메인 흐름 */}
				<div className="relative w-full h-[400px] flex items-center justify-center px-8">
					{/* 파이프라인 백본 */}
					<div className="absolute w-full h-2 bg-gradient-to-r from-blue-500 via-purple-500 to-yellow-500 rounded-full opacity-30"></div>
					<div className="absolute w-full h-1 bg-gradient-to-r from-blue-400 via-purple-400 to-yellow-400 rounded-full shadow-lg shadow-purple-500/30"></div>

					{/* 데이터 흐름 애니메이션 - 여러 개의 데이터 패킷 */}
					{[0, 1, 2].map((i) => {
						const stepWidth = window.innerWidth * 0.6
						const maxScroll = stepWidth * 2.5
						const progressPercent = (horizontalScrollPosition / maxScroll) * 100

						return (
							<div
								key={i}
								className="absolute w-4 h-4 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full shadow-lg"
								style={{
									left: `${Math.min(95, (progressPercent + i * 20) % 110)}%`,
									transform: 'translateX(-50%)',
									opacity: (progressPercent + i * 20) % 110 > 100 ? 0 : 0.8,
									transition: 'all 0.3s ease',
								}}
							>
								<div className="w-full h-full bg-white/30 rounded-full animate-ping"></div>
							</div>
						)
					})}

					{/* 진행 표시기 */}
					<div
						className="absolute h-1 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full transition-all duration-500 ease-out shadow-glow"
						style={{
							width: horizontalCompleted
								? '100%' // 횡 스크롤 완료 시 100%
								: `${Math.min(
										100,
										(horizontalScrollPosition / (window.innerWidth * 0.6 * 2.5)) * 100,
									)}%`,
							left: '0%',
						}}
					></div>

					{/* 프로세스 노드들 */}
					{[
						{
							step: 'INPUT',
							title: t('page.aiMotionStudio.p1'),
							description: t('page.aiMotionStudio.p2'),
							position: 10,
							color: 'blue',
						},
						{
							step: 'PROCESS',
							title: t('page.aiMotionStudio.p3'),
							description: t('page.aiMotionStudio.p4'),
							position: 35,
							color: 'purple',
						},
						{
							step: 'TRANSFORM',
							title: t('page.aiMotionStudio.p5'),
							description: t('page.aiMotionStudio.p6'),
							position: 65,
							color: 'pink',
						},
						{
							step: 'OUTPUT',
							title: t('page.aiMotionStudio.p7'),
							description: t('page.aiMotionStudio.p8'),
							position: 90,
							color: 'yellow',
						},
					].map((item, index) => {
						// 현재 진행도에 따른 활성화 계산 (새로운 maxScroll 기준)
						const stepWidth = window.innerWidth * 0.6
						const maxScroll = stepWidth * 2.5
						const progress = (horizontalScrollPosition / maxScroll) * 100

						// 디버깅을 위한 로그 (첫 번째 항목에서만)
						if (index === 0 && Math.random() < 0.1) {
							console.log('🔍 노드 상태 체크:', {
								horizontalCompleted,
								progress: progress.toFixed(1),
								position: item.position,
								itemStep: item.step,
							})
						}

						// 횡 스크롤 완료 상태에서는 모든 항목을 완료된 상태로 표시
						let isActive = false
						let isCompleted = false

						if (horizontalCompleted === true) {
							isActive = true
							isCompleted = true
						} else {
							isActive = progress >= item.position - 20 // 더 일찍 활성화
							isCompleted = progress >= item.position + 10 // 더 빨리 완료 표시
						}

						// 색상별 스타일 정의
						const colorStyles = {
							blue: {
								completed: 'from-blue-600 to-blue-800 border-blue-400 shadow-blue-500',
								border: 'border-blue-400',
								shadow: 'shadow-blue-500',
								text: 'text-blue-400',
							},
							purple: {
								completed:
									'from-purple-600 to-purple-800 border-purple-400 shadow-purple-500',
								border: 'border-purple-400',
								shadow: 'shadow-purple-500',
								text: 'text-purple-400',
							},
							pink: {
								completed: 'from-pink-600 to-pink-800 border-pink-400 shadow-pink-500',
								border: 'border-pink-400',
								shadow: 'shadow-pink-500',
								text: 'text-pink-400',
							},
							yellow: {
								completed:
									'from-yellow-600 to-yellow-800 border-yellow-400 shadow-yellow-500',
								border: 'border-yellow-400',
								shadow: 'shadow-yellow-500',
								text: 'text-yellow-400',
							},
						}

						const currentStyle = colorStyles[item.color as keyof typeof colorStyles]

						return (
							<div
								key={index}
								className="absolute"
								style={{ left: `${item.position}%`, transform: 'translateX(-50%)' }}
							>
								{/* 노드 원형 */}
								<div className="relative flex flex-col items-center">
									<div
										className={`w-20 h-20 rounded-full border-4 flex items-center justify-center mb-8 transition-all duration-500 cursor-pointer ${
											isCompleted
												? `bg-gradient-to-br ${currentStyle.completed} ${currentStyle.border} ${currentStyle.shadow}/50 shadow-lg scale-110 hover:scale-125 hover:rotate-12`
												: isActive
													? `bg-gradient-to-br ${currentStyle.completed}/70 ${currentStyle.border} ${currentStyle.shadow}/50 shadow-lg scale-105 animate-pulse hover:scale-115 hover:animate-none hover:rotate-6`
													: 'bg-gradient-to-br from-gray-800 to-gray-900 border-gray-600 shadow-gray-500/20 scale-100'
										}`}
									>
										<span
											className={`text-xs font-bold text-center leading-tight transition-colors duration-300 ${
												isCompleted
													? currentStyle.text
													: isActive
														? 'text-white'
														: 'text-gray-400'
											}`}
										>
											{item.step}
										</span>
									</div>

									{/* 노드 정보 카드 */}
									<div
										className={`backdrop-blur-sm rounded-xl p-4 border shadow-xl min-w-[280px] max-w-[320px] transition-all duration-300 hover:transform hover:scale-105 ${
											isActive
												? `bg-gray-800/95 ${currentStyle.border} ${currentStyle.shadow}/50 transform -translate-y-2`
												: 'bg-gray-800/80 border-gray-700/50 shadow-gray-900/50'
										}`}
									>
										<div
											className={`text-xs font-bold mb-2 transition-colors duration-300 ${
												isActive ? currentStyle.text : 'text-gray-500'
											}`}
										>
											{item.step}
										</div>
										<h4
											className={`text-lg font-bold mb-2 transition-colors duration-300 ${
												isActive ? 'text-white' : 'text-gray-400'
											}`}
										>
											{item.title}
										</h4>
										<p
											className={`text-sm leading-relaxed transition-colors duration-300 ${
												isActive ? 'text-gray-200' : 'text-gray-500'
											}`}
										>
											{item.description}
										</p>
									</div>
								</div>
							</div>
						)
					})}
				</div>

				{/* 파티클 효과 배경 */}
				<div className="absolute inset-0 overflow-hidden pointer-events-none">
					{[...Array(20)].map((_, i) => (
						<div
							key={i}
							className="absolute w-1 h-1 bg-purple-400/30 rounded-full animate-bounce"
							style={{
								left: `${Math.random() * 100}%`,
								top: `${Math.random() * 100}%`,
								animationDelay: `${Math.random() * 3}s`,
								animationDuration: `${2 + Math.random() * 2}s`,
							}}
						/>
					))}
				</div>

				{/* CSS 애니메이션을 위한 스타일 */}
				<style
					dangerouslySetInnerHTML={{
						__html: `
						@keyframes flow {
							0% { transform: translateX(-50%) scale(1); opacity: 1; }
							50% { transform: translateX(-50%) scale(1.2); opacity: 0.8; }
							100% { transform: translateX(-50%) scale(1); opacity: 1; }
						}
						
						.shadow-glow {
							box-shadow: 0 0 20px rgba(168, 85, 247, 0.4), 0 0 40px rgba(168, 85, 247, 0.2);
						}
					`,
					}}
				/>
			</section>

			{/* AI Pipeline 프로세스 섹션 - 모바일 */}
			<section
				data-section="process-mobile"
				className={`md:hidden w-full bg-gradient-to-br from-slate-900 via-purple-900/30 to-slate-900 py-20 transition-all duration-1000 ${
					visibleSections.includes('process-mobile')
						? 'opacity-100 translate-y-0'
						: 'opacity-0 translate-y-12'
				}`}
			>
				{/* 제목 */}
				<div
					className={`text-center mb-16 transition-all duration-1000 delay-200 ${
						visibleSections.includes('process-mobile')
							? 'opacity-100 translate-y-0'
							: 'opacity-0 translate-y-8'
					}`}
				>
					<h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
						{t('page.aiMotionStudio.t3')}
					</h3>
					<p className="text-lg text-gray-300 px-4">{t('page.aiMotionStudio.t4')}</p>
				</div>

				{/* 모바일 파이프라인 플로우 */}
				<div className="relative px-6 space-y-8">
					{/* 연결선 백그라운드 */}
					<div className="absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 via-purple-500 to-yellow-500 opacity-30 transform -translate-x-1/2 rounded-full"></div>

					{/* 프로세스 노드들 */}
					{[
						{
							step: 'INPUT',
							title: t('page.aiMotionStudio.p1'),
							description: t('page.aiMotionStudio.p2'),
							color: 'blue',
							delay: 400,
						},
						{
							step: 'PROCESS',
							title: t('page.aiMotionStudio.p3'),
							description: t('page.aiMotionStudio.p4'),
							color: 'purple',
							delay: 600,
						},
						{
							step: 'TRANSFORM',
							title: t('page.aiMotionStudio.p5'),
							description: t('page.aiMotionStudio.p6'),
							color: 'pink',
							delay: 800,
						},
						{
							step: 'OUTPUT',
							title: t('page.aiMotionStudio.p7'),
							description: t('page.aiMotionStudio.p8'),
							color: 'yellow',
							delay: 1000,
						},
					].map((item, index) => {
						// 색상별 스타일 정의 (데스크톱과 동일)
						const colorStyles = {
							blue: {
								completed: 'from-blue-600 to-blue-800 border-blue-400 shadow-blue-500',
								border: 'border-blue-400',
								shadow: 'shadow-blue-500',
								text: 'text-blue-400',
								bg: 'bg-blue-500/10',
							},
							purple: {
								completed:
									'from-purple-600 to-purple-800 border-purple-400 shadow-purple-500',
								border: 'border-purple-400',
								shadow: 'shadow-purple-500',
								text: 'text-purple-400',
								bg: 'bg-purple-500/10',
							},
							pink: {
								completed: 'from-pink-600 to-pink-800 border-pink-400 shadow-pink-500',
								border: 'border-pink-400',
								shadow: 'shadow-pink-500',
								text: 'text-pink-400',
								bg: 'bg-pink-500/10',
							},
							yellow: {
								completed:
									'from-yellow-600 to-yellow-800 border-yellow-400 shadow-yellow-500',
								border: 'border-yellow-400',
								shadow: 'shadow-yellow-500',
								text: 'text-yellow-400',
								bg: 'bg-yellow-500/10',
							},
						}

						const currentStyle = colorStyles[item.color as keyof typeof colorStyles]
						const isVisible = visibleSections.includes('process-mobile')

						return (
							<div
								key={index}
								className={`relative flex items-start space-x-4 transition-all duration-1000 delay-${
									item.delay
								} ${
									isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
								}`}
							>
								{/* 노드 원형 */}
								<div className="relative flex-shrink-0">
									<div
										className={`w-16 h-16 rounded-full border-4 flex items-center justify-center ${currentStyle.border} bg-gradient-to-br ${currentStyle.completed} ${currentStyle.shadow}/50 shadow-lg z-10 relative`}
									>
										<span
											className={`text-[10px] font-bold leading-tight ${currentStyle.text} text-center px-1`}
										>
											{item.step}
										</span>
									</div>
								</div>

								{/* 콘텐츠 카드 */}
								<div
									className={`flex-1 backdrop-blur-sm rounded-xl p-4 border ${currentStyle.border} ${currentStyle.shadow}/20 shadow-lg bg-gray-800/70 hover:bg-gray-800/90 transition-all duration-300`}
								>
									<div className={`text-xs font-bold mb-1 ${currentStyle.text}`}>
										STEP {index + 1}
									</div>
									<h4 className="text-lg font-bold text-white mb-2">{item.title}</h4>
									<p className="text-sm text-gray-300 leading-relaxed">
										{item.description}
									</p>
								</div>
							</div>
						)
					})}
				</div>

				{/* 파티클 효과 배경 - 모바일용 */}
				<div className="absolute inset-0 overflow-hidden pointer-events-none">
					{[...Array(10)].map((_, i) => (
						<div
							key={i}
							className="absolute w-1 h-1 bg-purple-400/20 rounded-full animate-bounce"
							style={{
								left: `${Math.random() * 100}%`,
								top: `${Math.random() * 100}%`,
								animationDelay: `${Math.random() * 3}s`,
								animationDuration: `${2 + Math.random() * 2}s`,
							}}
						/>
					))}
				</div>
			</section>

			{/* AI 이미지 광고 섹션 */}
			<section
				data-section="image-ads"
				className={`relative w-full py-20 transition-all duration-1000 ${
					visibleSections.includes('image-ads')
						? 'opacity-100 translate-y-0'
						: 'opacity-0 translate-y-12'
				}`}
			>
				{/* 배경 그라데이션 */}
				<div className="absolute inset-0 bg-gradient-to-r from-cyan-600/5 via-blue-600/5 to-indigo-600/5"></div>

				<div className="relative">
					{/* 좌우 분할 레이아웃 (반대 방향) */}
					<div className="min-h-screen flex flex-col lg:flex-row-reverse">
						{/* 우측 이미지 영역 */}
						<div className="lg:w-1/2 relative">
							<div
								className={`h-[40vh] lg:h-full transition-all duration-1000 delay-100 ${
									visibleSections.includes('image-ads')
										? 'opacity-100 translate-x-0'
										: 'opacity-0 translate-x-8'
								}`}
							>
								<div className="relative h-full overflow-hidden group">
									<img
										src={Img1}
										alt="AI 이미지 광고 쇼케이스"
										className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:brightness-110"
										onError={(e) => {
											const target = e.target as HTMLImageElement
											target.src =
												"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 400'%3E%3Crect width='100%25' height='100%25' fill='%23374151'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%23fff' font-size='24'%3EAI 이미지 광고 비주얼%3C/text%3E%3C/svg%3E"
										}}
									/>
									{/* 호버시 추가 오버레이 효과 */}
									<div className="absolute inset-0 bg-cyan-600/0 group-hover:bg-cyan-600/20 transition-all duration-500"></div>
									<div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-cyan-900/30"></div>
									<div className="absolute inset-0 flex items-center justify-center">
										<img
											src={LogoTakumi}
											alt="TAKUMI MOTION STUDIO"
											className="w-32 h-32 md:w-40 md:h-40 lg:w-1/5 lg:h-1/5 object-contain"
										/>
										{/* <div className="text-center">
											<div className="flex items-center justify-center space-x-2 text-white font-semibold mb-2 text-6xl md:text-8xl lg:text-[clamp(8rem,30vh,25rem)]">
												<span>匠</span>
											</div>
											<p className="text-white font-medium text-lg md:text-2xl lg:text-[clamp(1.5rem,5vh,4rem)] tracking-widest eng-bold">
												MOTION STUDIO
											</p>
										</div> */}
									</div>
								</div>
							</div>
						</div>

						{/* 좌측 콘텐츠 영역 */}
						<div className="lg:w-1/2 flex items-center justify-center px-6 lg:px-12 xl:px-20 py-12 lg:py-20">
							<div className="w-full max-w-2xl">
								<div
									className={`transition-all duration-1000 delay-200 ${
										visibleSections.includes('image-ads')
											? 'opacity-100 translate-x-0'
											: 'opacity-0 -translate-x-8'
									}`}
								>
									<h2 className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-400 bg-clip-text text-transparent mb-6 eng-bold">
										{t('page.aiMotionStudio.tms1')}
									</h2>
									<p className="text-2xl text-gray-300 mb-8">
										{t('page.aiMotionStudio.tms2')}
									</p>

									<h3 className="text-xl md:text-2xl text-gray-200 leading-relaxed mb-8">
										{t('page.aiMotionStudio.tms3')}
									</h3>

									<div className="bg-gray-800/30 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-cyan-500/20">
										<p className="text-lg text-gray-300 leading-relaxed">
											{t('page.aiMotionStudio.tms4')}{' '}
											<span className="font-semibold text-cyan-400">
												{t('page.aiMotionStudio.tms5')}
											</span>
										</p>
									</div>

									<p className="text-xl md:text-2xl text-gray-200 leading-relaxed my-8">
										{t('page.aiMotionStudio.tms6')}
										<br />
										{t('page.aiMotionStudio.tms7')}
									</p>

									<div className="grid grid-cols-2 md:grid-cols-3 gap-4 my-8">
										<div className="group relative overflow-hidden bg-gradient-to-br from-cyan-500/10 to-blue-600/10 backdrop-blur-sm rounded-xl p-5 border border-cyan-500/30 hover:border-cyan-400/60 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-cyan-500/20">
											<div className="absolute inset-0 bg-gradient-to-br from-cyan-500/0 to-cyan-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
											<div className="relative text-center">
												<div className="w-12 h-12 mx-auto mb-3 rounded-full bg-cyan-500/20 flex items-center justify-center">
													<svg
														className="w-6 h-6 text-cyan-400"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
														/>
													</svg>
												</div>
												<span className="text-sm font-bold text-cyan-300 tracking-wider">
													HIGH QUALITY
												</span>
											</div>
										</div>
										<div className="group relative overflow-hidden bg-gradient-to-br from-purple-500/10 to-pink-600/10 backdrop-blur-sm rounded-xl p-5 border border-purple-500/30 hover:border-purple-400/60 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-purple-500/20">
											<div className="absolute inset-0 bg-gradient-to-br from-purple-500/0 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
											<div className="relative text-center">
												<div className="w-12 h-12 mx-auto mb-3 rounded-full bg-purple-500/20 flex items-center justify-center">
													<svg
														className="w-6 h-6 text-purple-400"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
														/>
													</svg>
												</div>
												<span className="text-sm font-bold text-purple-300 tracking-wider">
													FREE COPYRIGHT
												</span>
											</div>
										</div>
										<div className="group relative overflow-hidden bg-gradient-to-br from-emerald-500/10 to-teal-600/10 backdrop-blur-sm rounded-xl p-5 border border-emerald-500/30 hover:border-emerald-400/60 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-emerald-500/20">
											<div className="absolute inset-0 bg-gradient-to-br from-emerald-500/0 to-emerald-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
											<div className="relative text-center">
												<div className="w-12 h-12 mx-auto mb-3 rounded-full bg-emerald-500/20 flex items-center justify-center">
													<svg
														className="w-6 h-6 text-emerald-400"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
														/>
													</svg>
												</div>
												<span className="text-sm font-bold text-emerald-300 tracking-wider">
													LOW COST
												</span>
											</div>
										</div>
										<div className="group relative overflow-hidden bg-gradient-to-br from-orange-500/10 to-amber-600/10 backdrop-blur-sm rounded-xl p-5 border border-orange-500/30 hover:border-orange-400/60 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-orange-500/20">
											<div className="absolute inset-0 bg-gradient-to-br from-orange-500/0 to-orange-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
											<div className="relative text-center">
												<div className="w-12 h-12 mx-auto mb-3 rounded-full bg-orange-500/20 flex items-center justify-center">
													<svg
														className="w-6 h-6 text-orange-400"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M13 10V3L4 14h7v7l9-11h-7z"
														/>
													</svg>
												</div>
												<span className="text-sm font-bold text-orange-300 tracking-wider">
													FAST PROCESS
												</span>
											</div>
										</div>
										<div className="group relative overflow-hidden bg-gradient-to-br from-rose-500/10 to-red-600/10 backdrop-blur-sm rounded-xl p-5 border border-rose-500/30 hover:border-rose-400/60 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-rose-500/20">
											<div className="absolute inset-0 bg-gradient-to-br from-rose-500/0 to-rose-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
											<div className="relative text-center">
												<div className="w-12 h-12 mx-auto mb-3 rounded-full bg-rose-500/20 flex items-center justify-center">
													<svg
														className="w-6 h-6 text-rose-400"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
														/>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
														/>
													</svg>
												</div>
												<span className="text-sm font-bold text-rose-300 tracking-wider">
													AUTO SOLUTION
												</span>
											</div>
										</div>
										<div className="group relative overflow-hidden bg-gradient-to-br from-indigo-500/10 to-violet-600/10 backdrop-blur-sm rounded-xl p-5 border border-indigo-500/30 hover:border-indigo-400/60 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-indigo-500/20">
											<div className="absolute inset-0 bg-gradient-to-br from-indigo-500/0 to-indigo-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
											<div className="relative text-center">
												<div className="w-12 h-12 mx-auto mb-3 rounded-full bg-indigo-500/20 flex items-center justify-center">
													<svg
														className="w-6 h-6 text-indigo-400"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
														/>
													</svg>
												</div>
												<span className="text-sm font-bold text-indigo-300 tracking-wider">
													PIPELINE
												</span>
											</div>
										</div>
									</div>

									<Link
										to="/contact"
										className="text-center block mt-6"
									>
										<div className="inline-flex flex-col sm:flex-row items-center px-4 sm:px-6 py-3 sm:py-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl sm:rounded-full text-white font-semibold text-base sm:text-lg shadow-lg hover:shadow-purple-500/25 transition-all duration-300 max-w-sm sm:max-w-none mx-auto">
											<span
												className={`text-center leading-tight ${i18n.language !== 'ko' ? 'text-sm' : ''}`}
											>
												{t('page.aiMotionStudio.tms6')}
											</span>
											<span className="mx-0 sm:mx-3 my-1 sm:my-0 transform rotate-90 sm:rotate-0">
												→
											</span>
											<span
												className={`text-center text-sm sm:text-base leading-tight ${i18n.language !== 'ko' ? 'text-sm' : ''}`}
											>
												{t('page.aiMotionStudio.tms7')}
											</span>
										</div>
									</Link>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* 하이퀄리티 AI 영상 광고 섹션 */}
			<section
				data-section="high-quality"
				className={`relative w-full py-20 transition-all duration-1000 ${
					visibleSections.includes('high-quality')
						? 'opacity-100 translate-y-0'
						: 'opacity-0 translate-y-12'
				}`}
			>
				{/* 배경 그라데이션 */}
				<div className="absolute inset-0 bg-gradient-to-r from-yellow-600/5 via-orange-600/5 to-red-600/5"></div>

				<div className="relative">
					{/* 좌우 분할 레이아웃 */}
					<div className="min-h-screen flex flex-col lg:flex-row">
						{/* 좌측 이미지 영역 */}
						<div className="lg:w-1/2 relative">
							<div
								className={`h-[40vh] lg:h-full transition-all duration-1000 delay-100 ${
									visibleSections.includes('high-quality')
										? 'opacity-100 translate-x-0'
										: 'opacity-0 -translate-x-8'
								}`}
							>
								<div className="relative h-full overflow-hidden group">
									<img
										src={Img2}
										alt="하이퀄리티 AI 영상 제작 과정"
										className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:brightness-110"
										onError={(e) => {
											const target = e.target as HTMLImageElement
											target.src =
												"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 400'%3E%3Crect width='100%25' height='100%25' fill='%23451a03'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%23fff' font-size='24'%3E하이퀄리티 AI 영상 제작%3C/text%3E%3C/svg%3E"
										}}
									/>
									{/* 호버시 추가 오버레이 효과 */}
									<div className="absolute inset-0 bg-orange-600/0 group-hover:bg-orange-600/20 transition-all duration-500"></div>
									<div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-orange-900/30"></div>
									<div className="absolute inset-0 flex items-center justify-center">
										<div className="text-center">
											<p className="text-white font-medium text-lg md:text-2xl lg:text-[clamp(1.5rem,5vh,4rem)] tracking-widest eng-bold">
												LLM AGENT
											</p>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* 우측 콘텐츠 영역 */}
						<div className="lg:w-1/2 flex items-center justify-center px-6 lg:px-12 xl:px-20 py-12 lg:py-20">
							<div className="w-full max-w-2xl">
								<div
									className={`transition-all duration-1000 delay-200 ${
										visibleSections.includes('high-quality')
											? 'opacity-100 translate-x-0'
											: 'opacity-0 translate-x-8'
									}`}
								>
									<h2 className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 bg-clip-text text-transparent mb-6">
										{t('page.aiMotionStudio.llm1')}
									</h2>
									<p className="text-2xl text-gray-300 mb-8">
										{t('page.aiMotionStudio.llm2')}
									</p>

									<h3 className="text-xl md:text-2xl text-gray-200 leading-relaxed mb-8">
										{t('page.aiMotionStudio.llm3')}
									</h3>

									<div className="bg-gray-800/30 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-orange-500/20">
										<p className="text-lg text-gray-300 leading-relaxed">
											{t('page.aiMotionStudio.llm4')}{' '}
											<span className="font-semibold text-orange-400">
												{t('page.aiMotionStudio.llm5')}
											</span>
										</p>
									</div>

									<div className="flex flex-col md:flex-row items-stretch justify-center gap-4 md:gap-6 my-8">
										{/* Step 1 */}
										<div className="group relative flex-1 bg-gradient-to-br from-orange-500/10 to-amber-600/10 backdrop-blur-sm rounded-xl p-6 border border-orange-500/30 hover:border-orange-400/60 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-orange-500/20 text-center flex flex-col">
											<div className="w-14 h-14 mx-auto mb-4 rounded-full bg-orange-500/20 flex items-center justify-center">
												<svg
													className="w-7 h-7 text-orange-400"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
													/>
												</svg>
											</div>
											<h4 className="text-lg font-bold text-orange-300 mb-2">
												{t('page.aiMotionStudio.llm6')}
											</h4>
											<p className="text-sm text-gray-400 flex-grow">
												{t('page.aiMotionStudio.llm7')}
											</p>
										</div>

										{/* Arrow 1 */}
										<div className="text-orange-400 text-2xl font-bold rotate-90 md:rotate-0 flex items-center justify-center">
											→
										</div>

										{/* Step 2 */}
										<div className="group relative flex-1 bg-gradient-to-br from-yellow-500/10 to-orange-600/10 backdrop-blur-sm rounded-xl p-6 border border-yellow-500/30 hover:border-yellow-400/60 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-yellow-500/20 text-center flex flex-col">
											<div className="w-14 h-14 mx-auto mb-4 rounded-full bg-yellow-500/20 flex items-center justify-center">
												<svg
													className="w-7 h-7 text-yellow-400"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
													/>
												</svg>
											</div>
											<h4 className="text-lg font-bold text-yellow-300 mb-2">
												{t('page.aiMotionStudio.llm8')}
											</h4>
											<p className="text-sm text-gray-400 flex-grow">
												{t('page.aiMotionStudio.llm9')}
											</p>
										</div>

										{/* Arrow 2 */}
										<div className="text-orange-400 text-2xl font-bold rotate-90 md:rotate-0 flex items-center justify-center">
											→
										</div>

										{/* Step 3 */}
										<div className="group relative flex-1 bg-gradient-to-br from-red-500/10 to-rose-600/10 backdrop-blur-sm rounded-xl p-6 border border-red-500/30 hover:border-red-400/60 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-red-500/20 text-center flex flex-col">
											<div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
												<svg
													className="w-7 h-7 text-red-400"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
													/>
												</svg>
											</div>
											<h4 className="text-lg font-bold text-red-300 mb-2">
												{t('page.aiMotionStudio.llm10')}
											</h4>
											<p className="text-sm text-gray-400">
												{t('page.aiMotionStudio.llm11')}
											</p>
											<p className="text-xs text-gray-500 mt-1">
												{t('page.aiMotionStudio.llm12')}
											</p>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>
		</div>
	)
}

export default AiMotionStudio
